import { HeroButton } from "@/api/generated/BMWInterfaces"
import LinkWithParams from "@/lib/common/LinkWithParams"
import colors from "@/lib/theme/colors"
import styled from "@emotion/styled"
import Image from "next/image"

// A color field is a string[]: one stop = a solid color, two or more = a
// gradient. Solid colors are wrapped in a linear-gradient layer so the same
// `background` shorthand works whether the value is a color or a real gradient.
// `transparent` is the exception — it paints nothing, so it passes through as-is.
const toLayer = (stops: string[] | undefined, fallback: string): string => {
  const clean = stops?.filter(Boolean) ?? []
  if (clean.length === 0) return fallback
  if (clean.length === 1 && clean[0] === "transparent") return `transparent`
  if (clean.length === 1) return `linear-gradient(${clean[0]}, ${clean[0]})`
  return `linear-gradient(89.04deg, ${clean.join(", ")})`
}

const hasStops = (stops: string[] | undefined): boolean => (stops?.filter(Boolean).length ?? 0) > 0

// Two or more stops make a real gradient. `border-color` can't take a gradient,
// so those need the background-clip technique; a single stop is a plain color.
const isGradient = (stops: string[] | undefined): boolean =>
  (stops?.filter(Boolean).length ?? 0) > 1

// First non-empty stop, or the fallback. Used for solid borders, where the value
// must be a plain color (a linear-gradient string is invalid in `border`).
const toColor = (stops: string[] | undefined, fallback: string): string =>
  stops?.filter(Boolean)[0] ?? fallback

// Hover/focus falls back to the base color when no active variant is provided.
const activeOr = (active: string[], base: string[]): string[] => (hasStops(active) ? active : base)

// How a button's border is painted. Only "gradient" couples the border to the
// `background` shorthand; "solid" and "none" leave the background as one
// independent layer, so the border never bleeds into the fill.
type BorderMode = "gradient" | "solid" | "none"

const baseBorder: Record<BorderMode, string> = {
  gradient:
    "border: 1px solid transparent; background: var(--cta-bg) padding-box, var(--cta-border) border-box;",
  solid: "border: 1px solid var(--cta-border-color); background: var(--cta-bg);",
  none: "border: none; background: var(--cta-bg);",
}

const hoverBorder: Record<BorderMode, string> = {
  gradient: "background: var(--cta-bg-active) padding-box, var(--cta-border-active) border-box;",
  solid: "border-color: var(--cta-border-color-active); background: var(--cta-bg-active);",
  none: "background: var(--cta-bg-active);",
}

// Shared styles for both the link and button renderings. Colors are supplied at
// runtime via CSS custom properties, which keeps hover/focus pure CSS.
const ctaCss = ({ $border }: { $border: BorderMode }): string => `
  display: flex;
  flex: 1 0 0;
  min-width: 0;
  height: 40px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 0 16px;
  border-radius: 6px;
  font-family: inherit;
  font-weight: 500;
  font-size: 18px;
  line-height: normal;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  backdrop-filter: blur(20px);
  transition: background 0.2s ease, color 0.2s ease;
  color: var(--cta-text);
  ${baseBorder[$border]}
  &:hover,
  &:focus-visible {
    color: var(--cta-text-active);
    ${hoverBorder[$border]}
  }
`

const StyledLink = styled(LinkWithParams, {
  shouldForwardProp: (prop) => prop !== "$border",
})<{ $border: BorderMode }>`
  ${ctaCss}
`

const StyledButton = styled.button<{ $border: BorderMode }>`
  ${ctaCss}
`

const IconImage = styled(Image)`
  height: 20px;
  width: 20px;
  object-fit: contain;
`

type Props = {
  button: HeroButton
  // Used only when the CMS href is empty, so the button falls back to the
  // existing behaviour instead of rendering a dead link.
  fallbackHref?: string
  onClick?: () => void
}

export default function HeroCtaButton({
  button,
  fallbackHref,
  onClick,
}: Props): React.ReactElement | null {
  if (!button?.isActive) return null

  // A gradient in either state forces the whole border into gradient mode, since
  // base and hover share one CSS rule and the technique can't be mixed per state.
  const borderIsGradient = isGradient(button.borderColor) || isGradient(button.activeBorderColor)
  const $border: BorderMode = !hasStops(button.borderColor) && !hasStops(button.activeBorderColor)
    ? "none"
    : borderIsGradient
      ? "gradient"
      : "solid"

  const borderVars: Record<string, string> = borderIsGradient
    ? {
        "--cta-border": toLayer(button.borderColor, "transparent"),
        "--cta-border-active": toLayer(
          activeOr(button.activeBorderColor, button.borderColor),
          "transparent",
        ),
      }
    : {
        "--cta-border-color": toColor(button.borderColor, "transparent"),
        "--cta-border-color-active": toColor(
          activeOr(button.activeBorderColor, button.borderColor),
          "transparent",
        ),
      }

  const styleVars = {
    "--cta-bg": toLayer(button.backgroundColor, "transparent"),
    "--cta-bg-active": toLayer(
      activeOr(button.activeBackgroundColor, button.backgroundColor),
      "transparent",
    ),
    ...borderVars,
    "--cta-text": button.textColor || colors.white,
    "--cta-text-active": button.activeTextColor || button.textColor || colors.white,
  } as React.CSSProperties

  const content = (
    <>
      <span>{button.text}</span>
      {button.icon?.imageUrl && (
        <IconImage src={button.icon.imageUrl} alt={button.icon.alt || ""} width={20} height={20} />
      )}
    </>
  )

  const href = button.href || fallbackHref

  return href ? (
    <StyledLink href={href} $border={$border} style={styleVars} onClick={onClick}>
      {content}
    </StyledLink>
  ) : (
    <StyledButton type="button" $border={$border} style={styleVars} onClick={onClick}>
      {content}
    </StyledButton>
  )
}
